import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { ArrowLeft, CalendarDays, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { ProjectSearchComboBox } from '@/components/shared/project-search-combo-box';
import type { ProjectOption } from '@/components/shared/project-search-combo-box';
import { LocationSearchComboBox } from '@/components/shared/location-search-combo-box';
import type { LocationOption } from '@/components/shared/location-search-combo-box';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { AssignmentTechPicker } from '@/components/crew/assignment-tech-picker';
import {
  useTechProfiles,
  useAvailability,
  useTechCertifications,
  useCreateAssignment,
} from '@/hooks/use-crew';
import type { AssignWarning } from '@/hooks/use-staffing-board';
import type { CreateAssignmentPayload, CrewAssignment } from '@/lib/types';
import { parseFormErrors } from '@/lib/form-errors';
import { formatDateOnly } from '@/lib/date-utils';
import { ConfirmAssignDialog } from '@/app/pages/staffing-board/confirm-assign-dialog';

interface ConfirmRequiredData {
  confirm_required: boolean;
  confirmation_token: string;
  warnings: AssignWarning[];
  can_override?: boolean;
}

export function CreateAssignmentPage() {
  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<LocationOption | null>(null);
  const [location, setLocation] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [role, setRole] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedTechId, setSelectedTechId] = useState<number | null>(null);
  const [pendingPayload, setPendingPayload] = useState<CreateAssignmentPayload | null>(null);
  const [confirmation, setConfirmation] = useState<ConfirmRequiredData | null>(null);

  const { data: techsData } = useTechProfiles({ page_size: 200, status: 'active' });
  const { data: availData } = useAvailability({
    date_from: startDate || undefined,
    date_to: endDate || undefined,
    page_size: 5000,
  });
  const { data: certsData } = useTechCertifications({ page_size: 1000 });
  const createMutation = useCreateAssignment();

  const techs = techsData?.results ?? [];
  const availability = availData?.results ?? [];
  const certifications = certsData?.results ?? [];

  const submitAssignment = (payload: CreateAssignmentPayload) => {
    createMutation.mutate(
      payload,
      {
        onSuccess: (response: CrewAssignment) => {
          if (response.warnings?.length) {
            toast.warning(
              response.warnings.map((warning) => warning.message).join(' • '),
            );
          }
          const techName = techs.find((tech) => tech.id === payload.tech)?.full_name ?? 'Technician';
          const dateRange = payload.end_date
            ? `${formatDateOnly(payload.start_date)}–${formatDateOnly(payload.end_date)}`
            : formatDateOnly(payload.start_date);
          toast.success(`${techName} assigned for ${dateRange}.`);
          navigate(`/crew/schedule?month=${payload.start_date.slice(0, 7)}`);
        },
        onError: (error: unknown) => {
          if (
            isAxiosError<ConfirmRequiredData>(error) &&
            error.response?.status === 409 &&
            error.response.data?.confirm_required
          ) {
            setPendingPayload(payload);
            setConfirmation(error.response.data);
            return;
          }
          const errors = parseFormErrors(error);
          const message = [
            ...errors.general,
            ...Object.values(errors.fields).flat(),
          ][0] ?? 'Unable to create assignment.';
          toast.error(message);
        },
      }
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTechId || !selectedProject || !startDate) return;

    submitAssignment({
      tech: selectedTechId,
      project: selectedProject.id,
      project_name: selectedProject.name,
      project_number: selectedProject.project_number || undefined,
      location_fk: selectedLocation?.id || undefined,
      location: location || undefined,
      start_date: startDate,
      end_date: endDate || undefined,
      role: role || undefined,
      notes: notes || undefined,
      status: 'scheduled',
    });
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <div>
          <div className="flex items-center gap-2">
            <CalendarDays className="h-7 w-7 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-900">Create Assignment</h1>
          </div>
          <p className="text-gray-600 mt-1">Assign a technician to a project</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Project Details */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Project Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Project *</Label>
              <ProjectSearchComboBox
                value={selectedProject}
                required
                onChange={(project) => {
                  setSelectedProject(project);
                  if (project?.location_fk && !selectedLocation) {
                    setSelectedLocation({ id: project.location_fk, name: project.location || '', type: project.location_fk_type || '' });
                  }
                }}
              />
            </div>
            <div className="space-y-2">
              <Label>Location</Label>
              <LocationSearchComboBox value={selectedLocation} onChange={setSelectedLocation} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="location">Specific Area / Details</Label>
              <Input
                id="location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g. East face, 3rd floor scaffolding"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Start Date *</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">End Date</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role / Position</Label>
              <Input
                id="role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder="e.g., Lead Rope Access Tech"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Tech Picker */}
        <Card
          data-required="true"
          data-required-empty={selectedTechId ? 'false' : 'true'}
          data-required-message="Technician is required."
          tabIndex={-1}
        >
          <CardHeader>
            <CardTitle className="text-base">Select Technician</CardTitle>
            {startDate && endDate && (
              <p className="text-xs text-gray-500">
                Showing availability for {startDate} to {endDate}
              </p>
            )}
          </CardHeader>
          <CardContent>
            <AssignmentTechPicker
              techs={techs}
              availability={availability}
              certifications={certifications}
              startDate={startDate}
              endDate={endDate}
              onSelect={setSelectedTechId}
              selectedTechId={selectedTechId}
            />
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={createMutation.isPending}
          >
            <Plus className="h-4 w-4 mr-2" />
            {createMutation.isPending ? 'Creating...' : 'Create Assignment'}
          </Button>
        </div>
      </form>
      <ConfirmAssignDialog
        open={confirmation !== null}
        warnings={confirmation?.warnings}
        canOverride={confirmation?.can_override}
        onConfirm={(overrideReason) => {
          if (!pendingPayload || !confirmation) return;
          submitAssignment({
            ...pendingPayload,
            confirmed: true,
            confirmation_token: confirmation.confirmation_token,
            override_reason: overrideReason,
          });
        }}
        onCancel={() => {
          setConfirmation(null);
          setPendingPayload(null);
        }}
        isPending={createMutation.isPending}
      />
    </div>
  );
}
